import React, { useEffect, useState } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getSurveyById, updateSurvey } from 'services/surveys';
import { getAllActive } from 'services/sites';
import UploadSurveyMainImage from './UploadSurveyMainImage';

import PropTypes from 'prop-types';

const EditSurveyForm = ({ id }) => {
  const [surveyData, setSurveyData] = useState({
    name: '',
    description: '',
    is_active: 1,
    survey_token: '',
    created_at: '',
    updated_at: '',
    image_url: '',
    site_id: '',
    show_description: false,
    show_main_image: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    getAllActive()
      .then(response => {
        if (response && Array.isArray(response)) {
          setSites(response);
        } else {
          setSites([]);
        }
      })
      .catch(() => setSites([]));
  }, []);

  useEffect(() => {
    if (!id) {
      toast.error('No se encontró el ID de la encuesta.', { theme: 'colored' });
      return;
    }
    setLoading(true);
    getSurveyById(id)
      .then(response => {
        if (response.result) {
          const {
            name,
            description,
            is_active,
            survey_token,
            created_at,
            updated_at,
            image_url,
            site_id,
            show_description,
            show_main_image
          } = response.survey;
          setSurveyData({
            name,
            description: description || '',
            is_active: Number(is_active),
            survey_token,
            created_at,
            updated_at,
            image_url,
            site_id: site_id ? String(site_id) : '',
            show_description: Boolean(show_description),
            show_main_image: Boolean(show_main_image)
          });
        } else {
          toast.error('No se encontró la encuesta.', { theme: 'colored' });
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error al obtener los datos de la encuesta:', error);
        toast.error('Error al cargar los datos de la encuesta.', {
          theme: 'colored'
        });
        setLoading(false);
      });
  }, [id]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setSurveyData({
      ...surveyData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
    setErrors({
      ...errors,
      [name]: ''
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!surveyData.name.trim()) newErrors.name = 'El nombre es requerido.';
    if (!surveyData.site_id) newErrors.site_id = 'El sitio es requerido.';
    return newErrors;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Por favor, completa todos los campos requeridos.', {
        theme: 'colored'
      });
      return;
    }

    updateSurvey(id, {
      name: surveyData.name,
      description:
        surveyData.description && surveyData.description.trim()
          ? surveyData.description.trim()
          : null,
      is_active: surveyData.is_active,
      site_id: surveyData.site_id,
      show_description: surveyData.show_description,
      show_main_image: surveyData.show_main_image
    })
      .then(response => {
        if (response.result) {
          toast.success(
            `Encuesta "${surveyData.name}" actualizada exitosamente.`,
            { theme: 'colored' }
          );
        } else {
          toast.error('Error al actualizar la encuesta.', { theme: 'colored' });
        }
      })
      .catch(error => {
        console.error('Error al actualizar la encuesta:', error);
        toast.error('Error al actualizar la encuesta.', { theme: 'colored' });
      });
  };

  const handleImageUploadSuccess = () => {
    toast.success('Imagen principal actualizada correctamente.', {
      theme: 'colored'
    });
    // Aquí podrías actualizar surveyData.image_url si quieres mostrar la nueva imagen al instante
  };

  if (loading) {
    return (
      <div className="text-center py-3">Cargando datos de la encuesta...</div>
    );
  }

  return (
    <Card>
      <Card.Header>
        <h5>Editar Encuesta</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          {/* Campos editables, uno por fila */}
          <Form.Group className="mb-3">
            <Form.Label>
              Nombre <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={surveyData.name}
              onChange={handleChange}
              placeholder="Nombre de la encuesta"
              isInvalid={!!errors.name}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={surveyData.description || ''}
              onChange={handleChange}
              placeholder="Descripción de la encuesta (opcional)"
              rows={2}
              isInvalid={!!errors.description}
              size="sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="show_description"
              name="show_description"
              checked={surveyData.show_description}
              onChange={handleChange}
              label="Mostrar esta descripción en la encuesta"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Sitio <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="site_id"
              value={surveyData.site_id}
              onChange={handleChange}
              isInvalid={!!errors.site_id}
              size="sm"
            >
              <option value="">Selecciona un sitio</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.site_id}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="show_main_image"
              name="show_main_image"
              checked={surveyData.show_main_image}
              onChange={handleChange}
              label="Mostrar imagen principal en la encuesta"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Estado</Form.Label>
            <div>
              <Form.Check
                type="checkbox"
                name="is_active"
                checked={surveyData.is_active === 1}
                onChange={handleChange}
                label={surveyData.is_active === 1 ? 'Activo' : 'Inactivo'}
                className="mt-2"
              />
            </div>
          </Form.Group>

          {/* Botón de guardar alineado a la izquierda */}
          <Button variant="primary" type="submit" size="sm">
            Guardar Cambios
          </Button>

          {/* Datos informativos */}
          <hr />
          <Form.Group className="mb-2">
            <Form.Label>Token</Form.Label>
            <Form.Control
              type="text"
              value={surveyData.survey_token || 'N/A'}
              readOnly
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Fecha de Creación</Form.Label>
            <Form.Control
              type="text"
              value={surveyData.created_at || 'N/A'}
              readOnly
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Última Actualización</Form.Label>
            <Form.Control
              type="text"
              value={surveyData.updated_at || 'N/A'}
              readOnly
              size="sm"
            />
          </Form.Group>
        </Form>
        <hr />

        {/* Imagen principal */}
        <UploadSurveyMainImage
          surveyId={id}
          onUploadSuccess={handleImageUploadSuccess}
          image_url={surveyData.image_url}
          show_main_image={surveyData.show_main_image}
        />

        {/* Gestión de reportes compartidos */}
      </Card.Body>
    </Card>
  );
};

EditSurveyForm.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default EditSurveyForm;
